import { useEffect, useRef, useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { routes, type PimTargetRecoveryReadback } from "@nova-ai/shared"
import { apiFetch } from "../lib/api"
import { queryKeys } from "./query-keys"

const VERIFICATION_INTERVAL_MS = 15000
const VERIFICATION_WINDOW_MS = 300000
const VERIFICATION_LIMIT = 20

type RecoveryVerification = {
  id: string
  targetIds: number[]
  remaining: number
  expiresAt: number
  active: boolean
  inFlight: boolean
  error: string | null
}

/** Page-local consent survives drawer reopening, never a reload or another operator session. */
export function useNutritionRecoverySession() {
  const [sessions, setSessions] = useState<RecoveryVerification[]>([])
  return { sessions, setSessions }
}
export type NutritionRecoverySession = ReturnType<typeof useNutritionRecoverySession>

export function useNutritionRecoveryVerification(
  session: NutritionRecoverySession,
  targetIds: number[],
  paused: boolean
) {
  const client = useQueryClient()
  const pending = useRef(new Set<string>())
  const revoked = useRef(new Set<string>())
  const previewRevisionRef = useRef(0)
  const [previewRevision, setPreviewRevision] = useState(0)
  const tick = useRef<() => void>(() => {})
  const { sessions, setSessions } = session
  const relevant = sessions.filter((item) => item.targetIds.some((id) => targetIds.includes(id)))
  const update = (id: string, patch: Partial<RecoveryVerification>) => {
    setSessions((items) => items.map((item) => (item.id === id ? { ...item, ...patch } : item)))
  }
  useEffect(() => {
    return client.getQueryCache().subscribe((event) => {
      if (
        event.type !== "updated" ||
        event.action.type !== "success" ||
        event.query.queryKey[0] !== "nutrition-recovery"
      ) {
        return
      }
      const data = client.getQueryData<PimTargetRecoveryReadback>(event.query.queryKey)
      if (!data || !targetIds.includes(data.targetId)) {
        return
      }
      if (data.state === "blocked" || data.state === "resolved") {
        revoked.current.add(data.id)
        setSessions((items) =>
          items.map((item) => (item.id === data.id ? { ...item, active: false } : item))
        )
      }
      if (data.state === "blocked") {
        // Count successful reads, even when Query preserves identical cached data.
        previewRevisionRef.current += 1
        setPreviewRevision(previewRevisionRef.current)
      }
    })
  }, [client, setSessions, targetIds])
  const verify = useMutation({
    mutationFn: async (id: string) => {
      const advanced = await apiFetch<PimTargetRecoveryReadback>(
        routes.pimEvents.recoveryVerify({ recoveryId: id }),
        { method: "POST" }
      )
      if (!advanced.ok) {
        throw new Error(advanced.error.message)
      }
      // Only a subsequent authoritative read may replace the displayed recovery result.
      const readback = await apiFetch<PimTargetRecoveryReadback>(
        routes.pimEvents.recoveryDetail({ recoveryId: id })
      )
      if (!readback.ok) {
        throw new Error(readback.error.message)
      }
      return readback.value
    },
    retry: false,
    onSuccess: async (data, id) => {
      await client.cancelQueries({ queryKey: queryKeys.nutritionRecovery(id) })
      client.setQueryData(queryKeys.nutritionRecovery(id), data)
      if (data.state === "resolved" || data.state === "blocked") {
        update(id, { active: false })
      }
      void client.invalidateQueries({ queryKey: ["nutrition-queue"] })
      void client.invalidateQueries({ queryKey: ["nutrition-queue-detail"] })
      void client.invalidateQueries({ queryKey: ["nutrition-queue-selection"] })
    },
    onError: (error, id) => update(id, { active: false, error: error.message }),
    onSettled: (_data, _error, id) => {
      pending.current.delete(id)
      update(id, { inFlight: false })
    },
  })
  const advance = (item: RecoveryVerification, explicitContinuation = false) => {
    if (
      paused ||
      document.visibilityState === "hidden" ||
      pending.current.has(item.id) ||
      item.inFlight
    ) {
      return
    }
    const stored = client.getQueryData<PimTargetRecoveryReadback>(
      queryKeys.nutritionRecovery(item.id)
    )
    if (
      !item.active ||
      revoked.current.has(item.id) ||
      item.remaining <= 0 ||
      item.expiresAt <= Date.now() ||
      stored?.state === "resolved" ||
      (stored?.state === "blocked" && !explicitContinuation)
    ) {
      revoked.current.add(item.id)
      update(item.id, { active: false })
      return
    }
    pending.current.add(item.id)
    update(item.id, { remaining: item.remaining - 1, inFlight: true })
    verify.mutate(item.id)
  }
  useEffect(() => {
    tick.current = () => relevant.filter((item) => item.active).forEach((item) => advance(item))
  })
  useEffect(() => {
    if (paused) {
      return
    }
    const timer = setInterval(() => tick.current(), VERIFICATION_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [paused])
  const authorize = (ids: string[]) => {
    const admitted = ids.map((id) => ({
      id,
      targetIds,
      remaining: VERIFICATION_LIMIT,
      expiresAt: Date.now() + VERIFICATION_WINDOW_MS,
      active: true,
      inFlight: false,
      error: null,
    }))
    setSessions((items) => [...items.filter((item) => !ids.includes(item.id)), ...admitted])
    return admitted
  }
  const continueVerification = (id: string, revision: number) => {
    if (
      revision !== previewRevisionRef.current ||
      paused ||
      document.visibilityState === "hidden" ||
      pending.current.has(id)
    ) {
      return
    }
    previewRevisionRef.current += 1
    setPreviewRevision(previewRevisionRef.current)
    revoked.current.delete(id)
    // This call alone may bypass the old blocked cache; no bypass survives it.
    authorize([id]).forEach((item) => advance(item, true))
  }
  return {
    sessions: relevant,
    authorize,
    advance: (item: RecoveryVerification) => advance(item),
    continueVerification,
    previewRevision,
    getPreviewRevision: () => previewRevisionRef.current,
    isPending: verify.isPending,
  }
}
